import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import UndoIcon from '@mui/icons-material/Undo'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  SxProps,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'

import {
  instructionSummaryApi,
  PendingSummary,
  SUMMARY_STATUS,
  UpdateSummaryRequest,
} from 'hooks/api/instructionSummary'

import { EditFormState, EditSummaryDialog } from './EditSummaryDialog'
import { PromptSettingSection } from './PromptSettingSection'
import { RelatedRecordsAccordion } from './RelatedRecordsAccordion'
import { SummaryContent } from './SummaryContent'

const INITIAL_EDIT_FORM: EditFormState = {
  recentSituation: '',
  nextCheckPoints: '',
  longTermTrend: '',
}

export function InstructionSummaryTab() {
  const [pendingSummaries, setPendingSummaries] = useState<PendingSummary[]>([])
  const [recentPublished, setRecentPublished] = useState<PendingSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<EditFormState>(INITIAL_EDIT_FORM)
  const [saving, setSaving] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const api = instructionSummaryApi()
      const [pendingRes, publishedRes] = await Promise.all([
        api.getPending(),
        api.getRecentPublished(),
      ])
      setPendingSummaries(pendingRes.data.summaries || [])
      setRecentPublished(publishedRes.data.summaries || [])
    } catch {
      setError('データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handlePublish = async (id: string) => {
    if (!confirm('このサマリを公開しますか？')) return
    try {
      setSaving(true)
      const api = instructionSummaryApi()
      await api.publish(id)
      await fetchData()
    } catch {
      alert('公開に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleUnpublish = async (id: string) => {
    if (!confirm('このサマリの公開を取り消しますか？\n確認待ちに戻ります。'))
      return
    try {
      setSaving(true)
      const api = instructionSummaryApi()
      await api.unpublish(id)
      await fetchData()
    } catch {
      alert('公開取り消しに失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const handleReject = async (id: string) => {
    if (
      !confirm('このサマリを却下しますか？\n警備員には表示されなくなります。')
    )
      return
    try {
      setSaving(true)
      const api = instructionSummaryApi()
      await api.reject(id)
      await fetchData()
    } catch {
      alert('却下に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const openEditDialog = (summary: PendingSummary) => {
    setEditingId(summary.summary.id)
    setEditForm({
      recentSituation: summary.summary.recentSituation,
      nextCheckPoints: summary.summary.nextCheckPoints,
      longTermTrend: summary.summary.longTermTrend,
    })
  }

  const closeEditDialog = () => {
    setEditingId(null)
    setEditForm(INITIAL_EDIT_FORM)
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    try {
      setSaving(true)
      const api = instructionSummaryApi()
      const data: UpdateSummaryRequest = {
        recentSituation: editForm.recentSituation,
        nextCheckPoints: editForm.nextCheckPoints,
        longTermTrend: editForm.longTermTrend,
      }
      await api.update(editingId, data)
      closeEditDialog()
      await fetchData()
    } catch {
      alert('保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={centerBox}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  return (
    <>
      {/* プロンプト設定セクション */}
      <PromptSettingSection />

      {/* 確認待ちセクション */}
      <Typography variant="h6" sx={titleStyle}>
        確認待ちAIサマリ
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={descStyle}>
        AIが生成したサマリを確認し、問題がなければ公開してください。
      </Typography>

      {pendingSummaries.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          確認待ちのサマリはありません
        </Alert>
      ) : (
        pendingSummaries.map((item) => (
          <Card key={item.summary.id} sx={cardStyle}>
            <CardContent>
              <Box sx={cardHeaderStyle}>
                <Box>
                  <Typography variant="h6">
                    {item.companyName} / {item.companyBranchName} /{' '}
                    {item.securityName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    生成日時:{' '}
                    {item.summary.generatedAt
                      ? new Date(item.summary.generatedAt).toLocaleString(
                          'ja-JP',
                        )
                      : '-'}
                  </Typography>
                </Box>
                <Box sx={buttonGroupStyle}>
                  {item.summary.status === SUMMARY_STATUS.FAILED ? (
                    <Chip label="失敗" color="error" size="small" />
                  ) : (
                    <Chip label="確認待ち" color="warning" size="small" />
                  )}
                  <IconButton
                    size="small"
                    onClick={() => openEditDialog(item)}
                    title="編集"
                  >
                    <EditIcon />
                  </IconButton>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<BlockIcon />}
                    onClick={() => handleReject(item.summary.id)}
                    disabled={saving}
                  >
                    却下
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => handlePublish(item.summary.id)}
                    disabled={
                      saving || item.summary.status === SUMMARY_STATUS.FAILED
                    }
                  >
                    公開
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
              <SummaryContent item={item} />
              <RelatedRecordsAccordion summaryId={item.summary.id} />
            </CardContent>
          </Card>
        ))
      )}

      {/* 直近公開セクション */}
      <Divider sx={{ my: 4 }} />

      <Typography variant="h6" sx={titleStyle}>
        直近公開したサマリ（10件）
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={descStyle}>
        間違えて公開した場合は取り消しできます。
      </Typography>

      {recentPublished.length === 0 ? (
        <Alert severity="info">公開済みのサマリはありません</Alert>
      ) : (
        recentPublished.map((item) => (
          <Card key={item.summary.id} sx={publishedCardStyle}>
            <CardContent>
              <Box sx={cardHeaderStyle}>
                <Box>
                  <Typography variant="h6">
                    {item.companyName} / {item.companyBranchName} /{' '}
                    {item.securityName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    生成日時:{' '}
                    {item.summary.generatedAt
                      ? new Date(item.summary.generatedAt).toLocaleString(
                          'ja-JP',
                        )
                      : '-'}
                  </Typography>
                </Box>
                <Box sx={buttonGroupStyle}>
                  {item.summary.status === SUMMARY_STATUS.REJECTED ? (
                    <Chip label="却下" color="error" size="small" />
                  ) : (
                    <>
                      <Chip label="公開済み" color="success" size="small" />
                      <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        startIcon={<UndoIcon />}
                        onClick={() => handleUnpublish(item.summary.id)}
                        disabled={saving}
                      >
                        取り消し
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
              <SummaryContent item={item} />
              <RelatedRecordsAccordion summaryId={item.summary.id} />
            </CardContent>
          </Card>
        ))
      )}

      {/* 編集ダイアログ */}
      <EditSummaryDialog
        open={editingId !== null}
        editForm={editForm}
        saving={saving}
        onClose={closeEditDialog}
        onSave={handleSaveEdit}
        onFormChange={setEditForm}
      />
    </>
  )
}

const titleStyle: SxProps = {
  fontWeight: 'bold',
  mb: 1,
}

const descStyle: SxProps = {
  mb: 2,
}

const centerBox: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  py: 4,
}

const cardStyle: SxProps = {
  mb: 2,
}

const publishedCardStyle: SxProps = {
  mb: 2,
  opacity: 0.85,
}

const cardHeaderStyle: SxProps = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
}

const buttonGroupStyle: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}
